import { NoSymbolIcon } from '@heroicons/react/24/outline';
import { useState } from 'react';

import { Dialog } from '../../../../../common/Dialog';
import { Icon } from '../../../../../common/Icon';
import { formatMarkdownContent } from '../../../../../common/TipTapEditor';
import { TextLink } from '../../../../../components/TextLink';
import { BlockerItem } from '../types';

function BlockerDetailView({ blocker }: { blocker: BlockerItem }): JSX.Element {
  const renderLinks = (content: string): (string | JSX.Element)[] => {
    const urlRegex = /(https?:\/\/[^\s]+)/g;
    return content.split(urlRegex).map((part) =>
      urlRegex.test(part) ? (
        <a
          key={part}
          href={part}
          target="_blank"
          rel="noopener noreferrer"
          className="text-blue-600   hover:underline break-all"
        >
          {part}
        </a>
      ) : (
        part
      ),
    );
  };

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-semibold mb-2">Progress</h3>
        <div className="text-gray-700 whitespace-pre-wrap ">
          {renderLinks(formatMarkdownContent(blocker.ticketDescription))}
        </div>
      </div>
      <div>
        <h3 className="text-lg font-semibold mb-2">Blocker/Concerns</h3>
        <div className="text-gray-700 whitespace-pre-wrap ">
          {renderLinks(formatMarkdownContent(blocker.description))}
        </div>
      </div>
      <div>
        <h3 className="text-lg font-semibold mb-2">Reported By</h3>
        <p className="text-gray-700">
          {blocker.user.firstName} {blocker.user.lastName} on{' '}
          {blocker.dateAdded}
        </p>
      </div>
    </div>
  );
}

export function Blockers({
  skipHeader = false,
  skipAddedBy = false,
  blockers,
  isLoading,
  error,
}: {
  skipHeader?: boolean;
  skipAddedBy?: boolean;
  blockers: BlockerItem[];
  isLoading: boolean;
  error: Error | null;
}): JSX.Element {
  const [selectedBlocker, setSelectedBlocker] = useState<BlockerItem | null>(
    null,
  );

  if (isLoading) {
    return <div>Loading blockers...</div>;
  }

  if (error) {
    return <div>Error loading blockers: {error.message}</div>;
  }

  return (
    <div>
      {!skipHeader && (
        <div className="flex items-center gap-2 mb-4">
          <div className="w-5 h-5">
            <NoSymbolIcon className="text-primary" />
          </div>
          <h3 className="text-xl font-semibold">Blockers</h3>
        </div>
      )}

      {blockers.length === 0 ? (
        <div className="flex items-center justify-start gap-2  text-[#141414]">
          <Icon name="confetti" className="w-4 h-4" /> All clear — No blockers
          right now!
        </div>
      ) : (
        <ul className="list-disc space-y-4 ml-11">
          {blockers.map((blocker) => (
            <li key={blocker.id} className="pl-1">
              <TextLink
                className="text-lg cursor-pointer flex flex-row items-center gap-2"
                onClick={() => {
                  if (blocker?.externalLink) {
                    window.open(blocker.externalLink, '_blank');
                  }
                }}
              >
                {blocker.title} <Icon name="link" />
              </TextLink>
              <ul className="list-disc ml-4 mt-1  pb-4">
                <div className="flex flex-row items-center">
                  <li>
                    <span className="text-sm whitespace-pre-wrap">
                      {formatMarkdownContent(blocker.description)}
                    </span>
                    <span
                      className="text-[#CE3F4F] pl-1 cursor-pointer flex-shrink-0"
                      onClick={() => {
                        setSelectedBlocker(blocker);
                      }}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          setSelectedBlocker(blocker);
                        }
                      }}
                      role="button"
                      tabIndex={0}
                    >
                      show more
                    </span>
                  </li>
                  {!skipAddedBy && (
                    <span className="text-gray-400 pl-2">
                      (Added on {blocker.dateAdded} by {blocker.user.firstName}{' '}
                      {blocker.user.lastName})
                    </span>
                  )}
                </div>
              </ul>
            </li>
          ))}
        </ul>
      )}

      <Dialog
        isOpen={!!selectedBlocker}
        onClose={() => setSelectedBlocker(null)}
        title={
          selectedBlocker
            ? `${selectedBlocker.checkIn.ticket.ticketNumber}: ${selectedBlocker.checkIn.ticket.title}`
            : 'Blocker Details'
        }
      >
        {selectedBlocker && <BlockerDetailView blocker={selectedBlocker} />}
      </Dialog>
    </div>
  );
}
